import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  IconButton,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { CalendarSyncConfig, Listing } from '../../types';
import { 
  useCalendarSyncStatus, 
  useEnableCalendarSync, 
  useDisableCalendarSync, 
  useManualCalendarSync 
} from '../../hooks/api/useCalendarSync';

interface CalendarSyncSettingsProps {
  listings: Listing[];
  onSyncComplete?: () => void;
  onSyncProgress?: (progress: any[]) => void;
}

interface ListingSyncState {
  [listingId: string]: {
    isExpanded: boolean;
    isSyncing: boolean;
    tempUrl: string;
  };
}

export const CalendarSyncSettings: React.FC<CalendarSyncSettingsProps> = ({
  listings,
  onSyncComplete,
}) => {
  const [listingStates, setListingStates] = useState<ListingSyncState>({});
  
  const enableSyncMutation = useEnableCalendarSync();
  const disableSyncMutation = useDisableCalendarSync();
  const manualSyncMutation = useManualCalendarSync();

  const updateListingState = (listingId: string, updates: Partial<ListingSyncState[string]>) => {
    setListingStates(prev => ({
      ...prev,
      [listingId]: {
        ...prev[listingId],
        ...updates,
      },
    }));
  };

  const getListingState = (listingId: string) => {
    return listingStates[listingId] || {
      isExpanded: false,
      isSyncing: false,
      tempUrl: '',
    };
  };

  const handleToggleSync = async (listingId: string, enabled: boolean) => {
    const state = getListingState(listingId);
    
    if (enabled) {
      // Enable sync - need URL
      if (!state.tempUrl) {
        updateListingState(listingId, { isExpanded: true });
        return;
      }
      
      try {
        await enableSyncMutation.mutateAsync({
          listingId,
          request: {
            airbnbCalendarUrl: state.tempUrl,
            syncFrequency: 'daily'
          }
        });
        onSyncComplete?.();
      } catch (error) {
        console.error('Failed to enable sync:', error);
      }
    } else {
      // Disable sync
      try {
        await disableSyncMutation.mutateAsync(listingId);
        onSyncComplete?.();
      } catch (error) {
        console.error('Failed to disable sync:', error);
      }
    }
  };

  const handleUrlChange = (listingId: string, url: string) => {
    updateListingState(listingId, { tempUrl: url });
  };

  const handleUrlSave = async (listingId: string) => {
    const state = getListingState(listingId);
    
    try {
      await enableSyncMutation.mutateAsync({
        listingId,
        request: {
          airbnbCalendarUrl: state.tempUrl,
          syncFrequency: 'daily'
        }
      });
      onSyncComplete?.();
    } catch (error) {
      console.error('Failed to save URL:', error);
    }
  };

  const handleFrequencyChange = async (listingId: string, frequency: CalendarSyncConfig['syncFrequency']) => {
    const state = getListingState(listingId);
    
    try {
      await enableSyncMutation.mutateAsync({
        listingId,
        request: {
          airbnbCalendarUrl: state.tempUrl,
          syncFrequency: frequency
        }
      });
      onSyncComplete?.();
    } catch (error) {
      console.error('Failed to update frequency:', error);
    }
  };

  const handleManualSync = async (listingId: string) => {
    try {
      await manualSyncMutation.mutateAsync({ listingId });
      onSyncComplete?.();
    } catch (error) {
      console.error('Failed to sync:', error);
    }
  };

  const getSyncStatusIcon = (config: CalendarSyncConfig) => {
    switch (config.syncStatus) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'syncing':
        return <CircularProgress size={20} />;
      default:
        return <ScheduleIcon color="action" />;
    }
  };

  const getSyncStatusText = (config: CalendarSyncConfig) => {
    switch (config.syncStatus) {
      case 'success':
        return `Last synced: ${config.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString() : 'Never'}`;
      case 'error':
        return 'Sync failed';
      case 'syncing':
        return 'Syncing...';
      default:
        return 'Ready to sync';
    }
  };

  const isValidAirbnbUrl = (url: string) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('airbnb') && url.includes('calendar');
    } catch {
      return false;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Calendar Sync Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your Airbnb calendars to automatically sync external bookings
      </Typography>

      {listings.map((listing) => {
        return (
          <ListingSyncCard 
            key={listing.listingId} 
            listing={listing}
            listingState={getListingState(listing.listingId)}
            onStateUpdate={(updates) => updateListingState(listing.listingId, updates)}
            onToggleSync={(enabled) => handleToggleSync(listing.listingId, enabled)}
            onUrlSave={() => handleUrlSave(listing.listingId)}
            onFrequencyChange={(frequency) => handleFrequencyChange(listing.listingId, frequency)}
            onManualSync={() => handleManualSync(listing.listingId)}
            isLoading={enableSyncMutation.isPending || disableSyncMutation.isPending || manualSyncMutation.isPending}
          />
        );
      })}
      {listings.length === 0 && (
        <Alert severity="info">
          No listings found. Create a listing first to configure calendar sync.
        </Alert>
      )}
    </Box>
  );
};

interface ListingSyncCardProps {
  listing: Listing;
  listingState: ListingSyncState[string];
  onStateUpdate: (updates: Partial<ListingSyncState[string]>) => void;
  onToggleSync: (enabled: boolean) => void;
  onUrlSave: () => void;
  onFrequencyChange: (frequency: CalendarSyncConfig['syncFrequency']) => void;
  onManualSync: () => void;
  isLoading: boolean;
}

const ListingSyncCard: React.FC<ListingSyncCardProps> = ({
  listing,
  listingState,
  onStateUpdate,
  onToggleSync,
  onUrlSave,
  onFrequencyChange,
  onManualSync,
  isLoading,
}) => {
  const { data: syncStatus, isLoading: statusLoading } = useCalendarSyncStatus(listing.listingId);
  
  const config = syncStatus?.config;

  const isValidAirbnbUrl = (url: string) => {
    if (!url) return false;
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.includes('airbnb') && url.includes('calendar');
    } catch {
      return false;
    }
  };

  const hasValidUrl = isValidAirbnbUrl(listingState.tempUrl);
  const urlChanged = listingState.tempUrl !== (config?.airbnbCalendarUrl || '');

  const getSyncStatusIcon = (config?: CalendarSyncConfig) => {
    if (!config) return <ScheduleIcon color="action" />;
    
    switch (config.syncStatus) {
      case 'success':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'syncing':
        return <CircularProgress size={20} />;
      default:
        return <ScheduleIcon color="action" />;
    }
  };

  const getSyncStatusText = (config?: CalendarSyncConfig) => {
    if (!config) return 'Not configured';
    
    switch (config.syncStatus) {
      case 'success':
        return `Last synced: ${config.lastSyncAt ? new Date(config.lastSyncAt).toLocaleString() : 'Never'}`;
      case 'error':
        return 'Sync failed';
      case 'syncing':
        return 'Syncing...';
      default:
        return 'Ready to sync';
    }
  };

  // Initialize temp URL from config when component mounts
  React.useEffect(() => {
    if (config?.airbnbCalendarUrl && !listingState.tempUrl) {
      onStateUpdate({ tempUrl: config.airbnbCalendarUrl });
    }
  }, [config?.airbnbCalendarUrl, listingState.tempUrl, onStateUpdate]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {listing.title}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              {statusLoading ? <CircularProgress size={16} /> : getSyncStatusIcon(config)}
              <Typography variant="body2" color="text.secondary">
                {getSyncStatusText(config)}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={config?.enabled || false}
                  onChange={(e) => onToggleSync(e.target.checked)}
                  disabled={isLoading || statusLoading}
                />
              }
              label="Enable Sync"
            />
            <IconButton
              onClick={() => onStateUpdate({ isExpanded: !listingState.isExpanded })}
              disabled={isLoading || statusLoading}
            >
              {listingState.isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        <Collapse in={listingState.isExpanded}>
          <Divider sx={{ my: 2 }} />
          
          {/* Airbnb Calendar URL Input */}
          <Box mb={3}>
            <TextField
              fullWidth
              label="Airbnb Calendar URL"
              placeholder="https://www.airbnb.com/calendar/ical/..."
              value={listingState.tempUrl}
              onChange={(e) => onStateUpdate({ tempUrl: e.target.value })}
              error={Boolean(listingState.tempUrl && !hasValidUrl)}
              helperText={
                listingState.tempUrl && !hasValidUrl
                  ? 'Please enter a valid Airbnb calendar URL'
                  : 'Get this URL from your Airbnb calendar export settings'
              }
              disabled={isLoading || statusLoading}
              InputProps={{
                endAdornment: urlChanged && hasValidUrl && (
                  <Button
                    size="small"
                    onClick={onUrlSave}
                    disabled={isLoading || statusLoading}
                  >
                    Save
                  </Button>
                ),
              }}
            />
          </Box>

          {/* Sync Frequency */}
          <Box mb={3}>
            <FormControl fullWidth>
              <InputLabel>Sync Frequency</InputLabel>
              <Select
                value={config?.syncFrequency || 'daily'}
                label="Sync Frequency"
                onChange={(e) => onFrequencyChange(e.target.value as CalendarSyncConfig['syncFrequency'])}
                disabled={isLoading || statusLoading || !config?.enabled}
              >
                <MenuItem value="hourly">Every Hour</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="manual">Manual Only</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Manual Sync Button */}
          <Box mb={2}>
            <Button
              variant="outlined"
              startIcon={config?.syncStatus === 'syncing' ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={onManualSync}
              disabled={
                isLoading ||
                statusLoading ||
                !config?.enabled ||
                !config?.airbnbCalendarUrl ||
                config?.syncStatus === 'syncing'
              }
            >
              {config?.syncStatus === 'syncing' ? 'Syncing...' : 'Sync Now'}
            </Button>
          </Box>

          {/* Error Message */}
          {config?.errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Sync Error:</strong> {config.errorMessage}
              </Typography>
            </Alert>
          )}

          {/* Sync Results */}
          {syncStatus?.lastSyncResult && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Last Sync:</strong> Found {syncStatus.lastSyncResult.bookingsFound} bookings, 
                added {syncStatus.lastSyncResult.bookingsAdded}, 
                updated {syncStatus.lastSyncResult.bookingsUpdated}
                {syncStatus.lastSyncResult.errors && syncStatus.lastSyncResult.errors.length > 0 && (
                  <span> ({syncStatus.lastSyncResult.errors.length} errors)</span>
                )}
              </Typography>
            </Alert>
          )}

          {/* Sync Status Chip */}
          {config?.enabled && (
            <Box>
              <Chip
                label={`Status: ${config.syncStatus}`}
                color={
                  config.syncStatus === 'success'
                    ? 'success'
                    : config.syncStatus === 'error'
                    ? 'error'
                    : 'default'
                }
                size="small"
              />
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default CalendarSyncSettings;